import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, HStack, Image, rem, Skeleton, SkeletonCircle, Text, VStack } from '@bonusly/design-system';

import DeflatedBalloon from '../../../../components/celebrations/assets/deflated-balloon.svg';
import { CelebrationGroup } from '../../../../components/celebrations/types';
import { PanelCelebrationItem } from './panel-celebration-item';

interface PanelCelebrationsGroupProps {
	celebrations: CelebrationGroup;
	hasError?: boolean;
	isLoading?: boolean;
	onClose: () => void;
}

export const PanelCelebrationsGroup = ({ celebrations, hasError, isLoading, onClose }: PanelCelebrationsGroupProps): React.ReactElement => {
	const { t } = useTranslation('celebrations');

	if (isLoading) {
		return (
			<Box mt={8} data-testid="loading-state">
				<CelebrationItemSkeleton />
				<CelebrationItemSkeleton />
				<CelebrationItemSkeleton />
			</Box>
		);
	}

	if (hasError) {
		return (
			<Box px={2} alignContent="center">
				<Image height={rem(250)} margin="auto" pr={12} src={DeflatedBalloon} />
				<Text fontSize={100} fontWeight="normal" color="text.default" textAlign="center" pt={3} margin="auto" width="70%">
					{t('modal.error_message')}
				</Text>
			</Box>
		);
	}

	if (celebrations.count === 0) {
		return (
			<Box px={2} alignContent="center">
				<Image height={rem(250)} margin="auto" pr={12} src={DeflatedBalloon} />
				<Text fontSize={100} fontWeight="normal" color="text.default" textAlign="center" pt={3} margin="auto" width="70%">
					{t('modal.no_celebrations')}
				</Text>
			</Box>
		);
	}

	return (
		<Box>
			{celebrations.recent.length > 0 && (
				<Box mb={4}>
					<SectionHeading>{t('modal.recent')}</SectionHeading>
					<VStack as="ul" spacing={1} align="stretch" mt={2}>
						{celebrations.recent.map((celebration) => (
							<Box as="li" key={`panel-item-${celebration.id ?? `${celebration.user_id}-${celebration.type}`}`} listStyleType="none">
								<PanelCelebrationItem celebration={celebration} onClose={onClose} />
							</Box>
						))}
					</VStack>
				</Box>
			)}
			{celebrations.upcoming.length > 0 && (
				<Box mb={4}>
					<SectionHeading>{t('modal.upcoming')}</SectionHeading>
					<VStack as="ul" spacing={1} align="stretch" mt={2}>
						{celebrations.upcoming.map((celebration) => (
							<Box as="li" key={`panel-item-${celebration.id ?? `${celebration.user_id}-${celebration.type}`}`} listStyleType="none">
								<PanelCelebrationItem celebration={celebration} onClose={onClose} />
							</Box>
						))}
					</VStack>
				</Box>
			)}
		</Box>
	);
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
	<Text fontSize={100} fontWeight="semibold" color="text.default" lineHeight={5}>
		{children}
	</Text>
);

const CelebrationItemSkeleton: FC = () => {
	return (
		<HStack alignItems="flex-start" gap={3} p={3} mb={1}>
			<SkeletonCircle height={10} width={10} flexShrink={0} />
			<VStack spacing={1} alignItems="left" overflow="hidden" flexGrow={1}>
				<Skeleton height={4} width="40%" />
				<Skeleton height={3.5} width="65%" />
			</VStack>
		</HStack>
	);
};
